// src/components/Register.js
//
// B2 — Auth pair polish (briefs/auth-pair.md). Re-shelled to the same
// asymmetric horizontal split as Login: emerald hero panel + cream
// form panel. Brand-voice labels via i18n. Error state uses Alert
// severity="error" only — no raw red. The hero البيان calligraphy is
// component="div" + aria-hidden so the form heading "تأسيس حسابك"
// stays the only h1 on the route.

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isParentRole } from '../services/userService';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { MaghribiHeading, ZelligeBackdrop } from './sacred';

const Register = () => {
  const { t } = useTranslation();
  const { register, ROLES } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordsMismatch'));
      return;
    }

    setLoading(true);
    try {
      const cred = await register({
        email,
        password,
        displayName,
        role: ROLES.PARENT,
      });
      const role = ROLES.PARENT;
      navigate(isParentRole(role) ? '/parent/dashboard' : '/dashboard');
      void cred;
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/email-already-in-use') {
        setError(t('auth.emailInUse'));
      } else if (err?.code === 'auth/invalid-email') {
        setError(t('auth.invalidEmail'));
      } else if (err?.code === 'auth/weak-password') {
        setError(t('auth.passwordTooShort'));
      } else {
        setError(t('auth.registerFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={(th) => ({
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: th.palette.sacred.surface,
      })}
    >
      <ZelligeBackdrop />

      {/* Hero arch panel — same shape as Login */}
      <Box
        sx={(th) => ({
          position: 'relative',
          flex: { md: '1 1 48%' },
          minHeight: { xs: 'clamp(200px, 30dvh, 280px)', md: '100dvh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: th.palette.sacred.primary,
          color: th.palette.sacred.onPrimary,
          overflow: 'hidden',
          p: 'clamp(1rem, 4vw, 4rem)',
        })}
      >
        <Box
          aria-hidden
          className="medersa-pattern"
          sx={{
            position: 'absolute',
            inset: 0,
            '--motif-opacity': 0.25,
            '--motif-size': 'clamp(180px, 22vw, 340px)',
            '--motif-color': '#ffffff',
          }}
        />

        <Box
          sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}
          aria-hidden="true"
        >
          <MaghribiHeading
            variant="display"
            glow
            tone="onPrimary"
            component="div"
            sx={{
              fontSize: {
                xs: 'clamp(3rem, 14vw, 5rem)',
                md: 'clamp(5rem, 9vw, 8rem)',
              },
              color: 'inherit',
              mb: 2,
            }}
          >
            البيان
          </MaghribiHeading>
          <Box
            sx={{
              width: 'clamp(40px, 6vw, 80px)',
              height: '1px',
              bgcolor: 'sacred.secondary',
              opacity: 0.7,
              mx: 'auto',
              mb: { xs: 2, md: 3 },
            }}
          />
          <Typography
            sx={{
              fontFamily: 'var(--sacred-font-display)',
              fontSize: 'clamp(1rem, 2.2vw, 1.55rem)',
              opacity: 0.95,
            }}
          >
            نُورُ الحفظ، بيانُ الفهم
          </Typography>
        </Box>

        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: '-5%',
            right: '-5%',
            width: 'clamp(120px, 18vw, 260px)',
            aspectRatio: '1',
            opacity: 0.08,
          }}
        >
          <svg viewBox="0 0 100 100" style={{ fill: '#fff' }}>
            <path d="M50 0 L61 39 L100 50 L61 61 L50 100 L39 61 L0 50 L39 39 Z" />
          </svg>
        </Box>
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: '-8%',
            left: '-8%',
            width: 'clamp(160px, 22vw, 320px)',
            aspectRatio: '1',
            opacity: 0.05,
          }}
        >
          <svg viewBox="0 0 100 100" style={{ fill: '#fff' }}>
            <path d="M50 0 L61 39 L100 50 L61 61 L50 100 L39 61 L0 50 L39 39 Z" />
          </svg>
        </Box>
      </Box>

      {/* Form panel */}
      <Box
        sx={{
          flex: { md: '1 1 52%' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 'clamp(0.75rem, 2.5vh, 3rem) clamp(1.5rem, 4vw, 4rem)',
          minHeight: { xs: 'auto', md: '100dvh' },
          maxHeight: { md: '100dvh' },
          overflowY: 'auto',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 'min(460px, 92vw)' }}>
          <IconButton
            onClick={() => navigate('/')}
            sx={{
              mb: 'clamp(0.25rem, 1vh, 1rem)',
              color: 'sacred.onSurfaceVariant',
            }}
            aria-label={t('common.back')}
          >
            <ArrowForward sx={{ transform: 'scaleX(-1)' }} />
          </IconButton>

          <MaghribiHeading
            variant="headline"
            component="h1"
            sx={{
              fontSize: 'clamp(1.5rem, 4vw, 2.1rem)',
              lineHeight: 1.4,
              mb: 'clamp(0.5rem, 1.5vh, 1rem)',
            }}
          >
            {t('auth.registerHeading')}
          </MaghribiHeading>
          <Typography
            sx={{
              fontFamily: 'var(--sacred-font-body)',
              color: 'sacred.onSurfaceVariant',
              lineHeight: 1.6,
              mb: 'clamp(1rem, 2.5vh, 2rem)',
              fontSize: 'clamp(0.9rem, 1.8vw, 1rem)',
            }}
          >
            {t('auth.registerCaption')}
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)', borderRadius: 2 }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              size="small"
              label={t('auth.displayName')}
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
            <TextField
              size="small"
              label={t('auth.email')}
              type="email"
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              size="small"
              label={t('auth.password')}
              type="password"
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              size="small"
              label={t('auth.confirmPassword')}
              type="password"
              variant="outlined"
              fullWidth
              required
              sx={{ mb: 'clamp(0.75rem, 2vh, 1.5rem)' }}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                py: 'clamp(0.5rem, 1.2vh, 1rem)',
                fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
                fontFamily: 'var(--sacred-font-display)',
              }}
            >
              {loading
                ? <CircularProgress size={22} color="inherit" />
                : t('auth.register')}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 'clamp(1rem, 2.5vh, 2rem)' }}>
            <Typography
              variant="body2"
              sx={{ color: 'sacred.onSurfaceVariant' }}
            >
              {t('auth.alreadyHaveAccount')}{' '}
              <Link
                component={RouterLink}
                to="/login"
                sx={{ color: 'sacred.primary', fontWeight: 600 }}
                underline="hover"
              >
                {t('auth.signIn')}
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
